import { useRef, useEffect, useState } from "react";
import { Link, useNavigate, NavLink } from "react-router-dom";
import { Confirmation, Error } from "../Components/Modal";
import { useMutation, useQuery } from "react-query";
import { logout } from "../Services/Auth";
import { getNotifications, getUnreadNotificationCount } from "../Services/Notification";
import { getUnreadChatCount } from "../Services/Chat";
import {
  joinChatRoom,
  subscribeNewMessages,
  subscribeChatStatus,
  getSenderId,
  getMessageParticipantId,
  isViewingParticipant,
} from "../Utils/chatSocket";
import { useSelector } from "react-redux";
import moment from "moment/moment";
import Nav from 'react-bootstrap/Nav';
import '../styles/Redesign.css';

const getHeaderNotifIcon = (title = "") => {
  const value = title.toLowerCase();
  if (value.includes("signup") || value.includes("request")) return "fas fa-user-plus";
  if (value.includes("payout") || value.includes("payment")) return "fas fa-wallet";
  if (value.includes("ride") || value.includes("booking")) return "fas fa-truck-moving";
  if (value.includes("review") || value.includes("rating")) return "fas fa-star";
  return "fas fa-bell";
};

export default function Sidebar() {
  const navigate = useNavigate();
  const isActive = useRef(null);

  const { user } = useSelector((state) => state.slice);

  const navLinks = [
    {
      title: "Dashboard",
      to: "/dashboard",
      icon: "fas fa-th-large",
    },
    {
      title: "User Management",
      to: "/user-management",
      icon: "fas fa-cog",
    },
    {
      title: "Driver Management",
      to: "/mover-management",
      icon: "fas fa-truck",
    },
    {
      title: "Rides Management",
      to: "/ride-management",
      icon: "fas fa-truck-moving",
    },
    {
      title: "Earnings",
      to: "/earnings",
      icon: "fas fa-money-bill-wave",
    },
    {
      title: "Driver Payouts",
      to: "/driver-payouts",
      icon: "fas fa-wallet",
    },
    {
      title: "Feedbacks",
      to: "/feedbacks",
      icon: "fas fa-comment-alt",
    },
    {
      title: "Ride Reviews",
      to: "/ride-reviews",
      icon: "fas fa-star",
    },
    // {
    //   title: "Business Management",
    //   to: "/business-management",
    //   icon: "fas fa-briefcase",
    // },
    {
      title: "Vehicle Management",
      to: "/vehicle-management",
      icon: "fas fa-car",
    },
    {
      title: "Vehicle Type Management",
      to: "/vehicle-type-management",
      icon: "fas fa-car-alt",
    },
    {
      title: "Category Management",
      to: "/category-management",
      icon: "fas fa-tags",
    },
    {
      title: "Cancellation Reasons",
      to: "/cancellation-management",
      icon: "fas fa-window-close",
    },
    {
      title: "Promo Codes",
      to: "/promo-codes",
      icon: "fas fa-ticket-alt",
    },
    {
      title: "System Settings",
      to: "/settings",
      icon: "fas fa-cog",
    },
    {
      title: "Payment Logs",
      to: "/payment-logs",
      icon: "fas fas fa-money-check-alt",
    },
    {
      title: "Push Notifications",
      to: "/notification-management",
      icon: "fas fa-paper-plane",
    },
    {
      title: "Notification Templates",
      to: "/notification-templates",
      icon: "fas fa-copy",
    },
    {
      title: "Reports",
      to: "/reports",
      icon: "fas fa-comment-alt",
    },
    {
      title: "CMS Management",
      to: "/cms-management",
      icon: "fas fa-file-contract",
    },
    {
      title: "FAQ Management",
      to: "/faq-management",
      icon: "fas fa-question-circle",
    },
    {
      title: "Landing Page CMS",
      to: "/landing-cms",
      icon: "fas fa-film",
    },
    {
      title: "Contact Inquiries",
      to: "/contact-inquiries",
      icon: "fas fa-envelope-open-text",
    },
    {
      title: "App Landing Page",
      to: "/",
      icon: "fas fa-mobile-alt",
    },
  ];

  const { mutate, isLoading } = useMutation(() => logout(), {
    retry: false,
    onSuccess: (res) => {
      // console.log("logout Res: ", res.data);
      localStorage.clear();
      navigate("/superadmin");
    },
    onError: (err) => {
      // console.log("logout Err: ", err);
      Error(err.response.data?.message);
    },
  });

  const headerBadgeQueryOptions = {
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 60 * 1000,
  };

  const {
    isLoading: notificationLoading,
    data: notificationData,
    refetch: refetchNotifications,
  } = useQuery(["getNotifications"], () => getNotifications(1, 5), headerBadgeQueryOptions);

  const { data: unreadNotifData, refetch: refetchUnreadNotif } = useQuery(
    ["unreadNotificationCount"],
    () => getUnreadNotificationCount(),
    headerBadgeQueryOptions
  );

  const { data: unreadChatData, refetch: refetchUnreadChat } = useQuery(
    ["unreadChatCount"],
    () => getUnreadChatCount(),
    headerBadgeQueryOptions
  );

  const refreshHeaderBadges = () => {
    refetchNotifications();
    refetchUnreadNotif();
    refetchUnreadChat();
  };

  useEffect(() => {
    const adminId = user?._id?.toString();
    if (!adminId) return;

    joinChatRoom(adminId);

    const unsubscribeMessages = subscribeNewMessages((newMsg) => {
      if (getSenderId(newMsg) === adminId) return;
      if (isViewingParticipant(getMessageParticipantId(newMsg))) return;
      refetchUnreadChat();
    });
    const unsubscribeStatus = subscribeChatStatus(() => refetchUnreadChat());
    return () => {
      unsubscribeMessages();
      unsubscribeStatus();
    };
  }, [user?._id, refetchUnreadChat]);

  const unreadNotifCount = unreadNotifData?.data?.unreadCount ?? 0;
  // Header chat badge: unread customer/driver messages plus requests waiting in the queue.
  const unreadChatCount =
    (unreadChatData?.data?.unreadCount ?? 0) + (unreadChatData?.data?.pendingRequests ?? 0);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    if (window.innerWidth < 992) {
      document.body.classList.remove("menu-open");
      document.body.classList.add("menu-hide");
    }
  };

  useEffect(() => {
    closeSidebar();
  }, []);

  return (
    <>
      {/* Redesigned Header */}
      <header className="rd-header">
        <div className="rd-header-left">
          <button className="rd-menu-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <i className="fas fa-bars" />
          </button>
        </div>

        <div className="rd-header-right">
          <button
            className="rd-icon-btn"
            onClick={() => {
              refreshHeaderBadges();
              navigate("/chat");
            }}
          >
            <i className="fas fa-comment-dots" />
            {unreadChatCount > 0 && (
              <span className="rd-badge">{unreadChatCount}</span>
            )}
          </button>

          <div className="dropdown dropdown-notification">
            <button
              className="rd-icon-btn"
              data-bs-toggle="dropdown"
              onClick={refreshHeaderBadges}
            >
              <i className="fas fa-bell" />
              {unreadNotifCount > 0 && (
                <span className="rd-badge">{unreadNotifCount}</span>
              )}
            </button>
            <ul className="dropdown-menu dropdown-menu-end notificationDiv rd-notif-dropdown">
              <li className="rd-notif-dropdown__header">
                <h6>
                  Notifications
                  {unreadNotifCount > 0 && (
                    <span className="rd-notif-dropdown__count">{unreadNotifCount} new</span>
                  )}
                </h6>
                <Link to="/notifications">View all</Link>
              </li>
              <li className="rd-notif-dropdown__list">
                {notificationData?.data?.data?.docs?.length > 0 ? (
                  notificationData.data.data.docs.map((item) => (
                    <Link
                      to="/notifications"
                      key={item._id}
                      className={`rd-notif-item ${item.read ? "" : "is-unread"}`}
                    >
                      <span className="rd-notif-item__icon">
                        <i className={getHeaderNotifIcon(item?.notification?.title)} />
                      </span>
                      <div className="rd-notif-item__content">
                        <p className="rd-notif-item__title">
                          {item?.notification?.title || "Notification"}
                        </p>
                        {item?.notification?.body ? (
                          <p className="rd-notif-item__body">{item.notification.body}</p>
                        ) : null}
                        <span className="rd-notif-item__time">
                          {moment(item?.createdAt).fromNow()}
                        </span>
                      </div>
                      {!item.read && <span className="rd-notif-item__dot" />}
                    </Link>
                  ))
                ) : (
                  <div className="rd-notif-empty">
                    <i className="fas fa-bell-slash" />
                    No notifications yet
                  </div>
                )}
              </li>
            </ul>
          </div>

          <div className="dropdown dropdown-user">
            <div className="rd-profile" data-bs-toggle="dropdown">
              <img
                src={user?.profileImage?.imageUrl ?? "images/avatar.png"}
                alt="User"
                className="rd-avatar"
              />
              <div className="rd-user-info" style={{ lineHeight: '1.2' }}>
                <span className="rd-user-name" style={{ display: 'block' }}>Hi, {user?.firstName ?? "Admin"}</span>
                <small className="rd-user-role" style={{ opacity: 0.8 }}>Admin</small>
              </div>
            </div>
            <div className="dropdown-menu dropdown-menu-right">
              <Link className="dropdown-item" to="/vehicle-type-management" onClick={closeSidebar}>
                <i className="fa fa-truck" /> Vehicle Type Management
              </Link>
              <Link className="dropdown-item" to="/profile" onClick={closeSidebar}>
                <i className="fa fa-user" /> Profile
              </Link>
              <Link className="dropdown-item" to="/payment-logs" onClick={closeSidebar}>
                <i className="fas fa-file-alt" /> Payment Logs
              </Link>
              <Link className="dropdown-item" to="/settings" onClick={closeSidebar}>
                <i className="fa fa-th-large" /> Settings
              </Link>
              <div className="dropdown-divider"></div>
              <button className="dropdown-item notBtn" onClick={() => Confirmation("Are you sure you want to logout?", "Yes", () => mutate())}>
                <i className="fas fa-sign-out-alt" /> Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Backdrop for Mobile Drawer */}
      {isSidebarOpen && (
        <div className="rd-sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
      )}

      {/* Redesigned Sidebar */}
      <aside className={`rd-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="rd-sidebar-header">
          <Link to="/dashboard">
            <img src="images/logo.png" alt="Logo" className="rd-logo" />
          </Link>
        </div>

        <div className="rd-sidebar-content">
          <div className="rd-nav-section">
            <div className="rd-nav-label">Main Menu</div>
            {navLinks.slice(0, 5).map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `rd-nav-item ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <i className={link.icon} />
                <span>{link.title}</span>
              </NavLink>
            ))}
          </div>

          <div className="rd-nav-section">
            <div className="rd-nav-label">Management</div>
            {navLinks.slice(5).map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `rd-nav-item ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <i className={link.icon} />
                <span>{link.title}</span>
              </NavLink>
            ))}
          </div>
        </div>

        <div className="rd-sidebar-footer">
          <button className="rd-logout-btn" onClick={() => Confirmation("Logout?", "Yes", () => mutate())}>
            <i className="fas fa-sign-out-alt" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Original Code Commented Out Below */}
      {/* 
      <nav className="header-navbar navbar-expand-md navbar navbar-with-menu fixed-top navbar-light navbar-border">
        <div className="navbar-wrapper">
          <div className="navbar-header">
            <ul className="nav navbar-nav flex-row">
              <li className="nav-item mobile-menu d-md-none mr-auto">
                <a
                  className="nav-link nav-menu-main menu-toggle hidden-xs"
                  href="#"
                >
                  <i className="ft-menu font-large-1" />
                </a>
              </li>
              <li className="nav-item">
                {" "}
                <Link className="navbar-brand" to="/dashboard">
                  {" "}
                  <img
                    className="brand-logo img-fluid"
                    alt="stack admin logo"
                    src="images/logo.png"
                  />{" "}
                </Link>{" "}
              </li>
              <li className="nav-item d-md-none">
                {" "}
                <a
                  className="nav-link open-navbar-container"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbar-mobile"
                >
                  <i className="fa fa-ellipsis-v" />
                </a>{" "}
              </li>
            </ul>
          </div>
          <div className="navbar-container content">
            <div className="collapse navbar-collapse" id="navbar-mobile">
              <ul className="nav navbar-nav me-auto float-start"></ul>
              <ul className="nav navbar-nav float-end nav-right align-items-center">
                <li className="dropdown dropdown-notification nav-item">
                  <button
                    className="notBtn nav-link nav-link-label"
                    onClick={() => navigate("/chat")}
                  >
                    <i className="fas fa-comment-dots" />
                  </button>
                </li>
                <li className="dropdown dropdown-notification nav-item">
                  <button
                    className="notBtn nav-link nav-link-label"
                    data-bs-toggle="dropdown"
                  >
                    <i className="fas fa-bell" />{" "}
                    <span className="badge badge-pill badge-default badge-danger badge-default badge-up">
                      {notificationData?.data?.data?.totalDocs &&
                        notificationData?.data?.data?.totalDocs}
                    </span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-right notificationDiv">
                    <li className="dropdown-menu-header d-flex align-items-baseline justify-content-between">
                      <h6>Notifications</h6>
                      {notificationData?.data?.data?.docs?.length > 0 && (
                        <Link to="/notifications" className="accentColor">
                          View All
                        </Link>
                      )}
                    </li>
                    <li className="scrollable-container media-list ps-container ps-theme-dark">
                      {notificationData?.data?.data?.docs?.length > 0 ? (
                        notificationData?.data?.data?.docs?.map((item) => (
                          <Link to="/notifications">
                            <div className="media d-flex">
                              <div className="media-left flex-shrink-0 align-self-top">
                                <i className="fas fa-bell" />
                              </div>
                              <div className="media-body flex-grow-1">
                                <h6 className="media-heading">
                                  {item?.notification?.title}
                                </h6>
                                <small>
                                  <time
                                    className="media-meta"
                                  >
                                    {moment(item?.createdAt).fromNow()}
                                  </time>
                                </small>
                              </div>
                            </div>
                          </Link>
                        ))
                      ) : (
                        <Link>
                          <div className="media d-flex">
                            <div className="media-body flex-grow-1">
                              <h6 className="media-heading">
                                No new notification
                              </h6>
                            </div>
                          </div>
                        </Link>
                      )}
                    </li>
                  </ul>
                </li>
                <li className="dropdown dropdown-user nav-item">
                  <a
                    className="dropdown-toggle nav-link dropdown-user-link"
                    data-bs-toggle="dropdown"
                  >
                    <span className="avatar avatar-online">
                      {" "}
                      <img
                        src={
                          user?.profileImage?.imageUrl ??
                          "images/avatar.png"
                        }
                        alt="avatar"
                      />{" "}
                    </span>
                    <div className="user-details">
                      <div className="userName">
                        Hi, {user?.firstName ?? "Mark"}
                      </div>
                      <div className="userRole">Admin</div>
                    </div>
                  </a>
                  <div className="dropdown-menu dropdown-menu-right">
                    <Link
                      className="dropdown-item"
                      to="/vehicle-type-management"
                      onClick={closeSidebar}
                    >
                      <i className="fa fa-truck" />
                      Vehicle Type Management
                    </Link>
                    <Link
                      className="dropdown-item"
                      to="/profile"
                      onClick={closeSidebar}
                    >
                      <i className="fa fa-user" />
                      Profile
                    </Link>
                    <Link
                      className="dropdown-item"
                      to="/payment-logs"
                      onClick={closeSidebar}
                    >
                      <i className="fas fa-file-alt" />
                      Payment Logs
                    </Link>
                    <Link
                      className="dropdown-item"
                      to="/settings"
                      onClick={closeSidebar}
                    >
                      <i className="fa fa-th-large" />
                      Settings
                    </Link>
                    <button
                      type="button"
                      className="dropdown-item notBtn"
                      onClick={() =>
                        Confirmation(
                          `Are you sure you want to logout from this platform`,
                          "Yes",
                          () => mutate()
                        )
                      }
                    >
                      <i className="fas fa-sign-out-alt" />
                      Sign Out
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      <div
        className="main-menu menu-fixed menu-light menu-accordion"
        data-scroll-to-active="true"
      >
        <div className="main-menu-content">
          <ul
            className="navigation"
            id="main-menu-navigation"
            data-menu="menu-navigation"
          >
            {navLinks?.map((item, index) => (
              <Nav.Item as="li" key={item.to}>
                <NavLink to={item.to} onClick={closeSidebar}>
                  <i className={item.icon} />
                  <span className="menu-title" data-i18n="">{item.title}</span>
                </NavLink>
              </Nav.Item>
            ))}
          </ul>
        </div>
      </div>
      */}
    </>
  );
}
